import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import type { CodeItem } from "@offsite/shared";

/**
 * Einmal-Code-Verwaltung für den Strategietag (`codeLogin`).
 *
 * Erzeugt Batches von Bühne-/Hintergrund-Codes und zeigt, welche schon
 * eingelöst sind — dafür pollt die Liste alle 5s, solange der Tab sichtbar
 * ist (der Moderator hat die Seite oft im Hintergrund-Tab offen, während der
 * Einlass läuft, und will nicht manuell neu laden).
 *
 * Der Druck-Button erzeugt Einlasskarten (eine pro Code) über das
 * Browser-Druckdialog: Bildschirm-UI wird per `@media print` ausgeblendet,
 * übrig bleibt ein Raster aus Karten mit Code, Login-Adresse und Rolle.
 */
export function AdminCodesSection() {
  const { state } = useStore();
  const token = state.auth.token;

  const [buehneAnzahl, setBuehneAnzahl] = useState("6");
  const [hintergrundAnzahl, setHintergrundAnzahl] = useState("60");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<CodeItem[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.admin.listCodes(token);
      setCodes(r.codes);
      setLoadError(null);
    } catch (err) {
      console.error(err);
      setLoadError("Codes konnten nicht geladen werden.");
    }
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Polling alle 5s, solange der Tab sichtbar ist — sonst zieht der
  // Hintergrund-Tab unnötig Anfragen, während der Moderator woanders schaut.
  useEffect(() => {
    let id: number | null = null;
    const start = () => {
      if (id !== null) return;
      id = window.setInterval(reload, 5000);
    };
    const stop = () => {
      if (id === null) return;
      window.clearInterval(id);
      id = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        reload();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [reload]);

  const generate = async () => {
    const buehne = Number(buehneAnzahl);
    const hintergrund = Number(hintergrundAnzahl);
    if (
      !Number.isInteger(buehne) ||
      !Number.isInteger(hintergrund) ||
      buehne < 0 ||
      hintergrund < 0 ||
      buehne + hintergrund === 0
    ) {
      setError("Bitte gültige Anzahlen angeben.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.admin.createCodes(token, {
        buehne,
        hintergrund,
        label: label.trim(),
      });
      await reload();
    } catch (err) {
      console.error(err);
      setError("Codes konnten nicht erzeugt werden.");
    } finally {
      setBusy(false);
    }
  };

  const buehneCodes = codes.filter((c) => c.rolle === "buehne");
  const hintergrundCodes = codes.filter((c) => c.rolle === "hintergrund");

  return (
    <div className="admin-grid" data-testid="admin-codes">
      <header className="admin-section-head">
        <h2>Einmal-Codes</h2>
        <p>
          Codes für den Einlass erzeugen — Bühne (6er-Gruppe live) und
          Hintergrund (alle anderen am eigenen Handy). Jeder Code funktioniert
          genau einmal.
        </p>
      </header>

      {error && (
        <div className="admin-callout" role="alert">
          {error}
        </div>
      )}
      {loadError && (
        <div className="admin-callout" role="alert">
          {loadError}
        </div>
      )}

      <div
        className="admin-toolbar"
        style={{
          padding: "14px 16px",
          background: "var(--svo-bg)",
          borderRadius: "var(--radius-xl)",
          flexWrap: "wrap",
        }}
      >
        <label className="login-field-label" style={{ display: "grid", gap: 4 }}>
          Anzahl Bühne
          <input
            className="input-text"
            style={{ width: 100 }}
            type="number"
            min="0"
            max="200"
            aria-label="Anzahl Bühne"
            value={buehneAnzahl}
            onChange={(e) => setBuehneAnzahl(e.target.value)}
          />
        </label>
        <label className="login-field-label" style={{ display: "grid", gap: 4 }}>
          Anzahl Hintergrund
          <input
            className="input-text"
            style={{ width: 100 }}
            type="number"
            min="0"
            max="200"
            aria-label="Anzahl Hintergrund"
            value={hintergrundAnzahl}
            onChange={(e) => setHintergrundAnzahl(e.target.value)}
          />
        </label>
        <label className="login-field-label" style={{ display: "grid", gap: 4, flex: 1 }}>
          Label
          <input
            className="input-text grow"
            aria-label="Label"
            placeholder="z. B. „Gruppe 1“"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn btn-primary"
          onClick={generate}
          disabled={busy}
          style={{ alignSelf: "flex-end" }}
        >
          {busy ? "Erzeuge…" : "Codes erzeugen"}
        </button>
      </div>

      <div className="admin-toolbar">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => window.print()}
          disabled={codes.length === 0}
        >
          🖨 Drucken
        </button>
        <span className="form-hint" style={{ margin: 0 }}>
          Eine Karte pro Code, Bühne zuerst.
        </span>
      </div>

      <CodeList title="Bühne" codes={buehneCodes} />
      <CodeList title="Hintergrund" codes={hintergrundCodes} />

      <PrintCards codes={[...buehneCodes, ...hintergrundCodes]} />
    </div>
  );
}

function CodeList({ title, codes }: { title: string; codes: CodeItem[] }) {
  return (
    <div className="admin-codes-list" data-testid={`admin-codes-list-${title.toLowerCase()}`}>
      <h3 style={{ margin: "8px 0" }}>
        {title} ({codes.length})
      </h3>
      {codes.length === 0 ? (
        <p className="form-hint">Noch keine Codes für {title}.</p>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {codes.map((c) => (
            <div key={c.code} className="admin-row" data-testid="admin-code-row">
              <code
                style={{
                  fontFamily: "monospace",
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                }}
              >
                {c.code}
              </code>
              <span>{c.label}</span>
              <span data-testid="admin-code-status">
                {c.usedAt
                  ? `eingelöst um ${formatTime(c.usedAt)}`
                  : "frei"}
              </span>
              {(c.name || c.abteilung) && (
                <span>
                  {c.name}
                  {c.name && c.abteilung ? " · " : ""}
                  {c.abteilung}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Nur für den Druck sichtbar (siehe `@media print` in app.css, das
 * `.admin-print-cards` freigibt und alles andere ausblendet).
 */
function PrintCards({ codes }: { codes: CodeItem[] }) {
  // Adresse aus der Offsite-Config statt aus einem Offsite-Ordner: Diese
  // Komponente ist geteilt — ein Import aus offsites/strategietag zöge
  // dessen Code in jedes Bundle und druckte in anderen Offsites die falsche
  // Adresse.
  const { subdomain } = useOffsiteConfig();
  return (
    <div className="admin-print-cards" aria-hidden="true">
      {codes.map((c) => (
        <div className="admin-print-card" key={c.code}>
          <div className="admin-print-card-code">{c.code}</div>
          <div className="admin-print-card-url">{subdomain}/m/login</div>
          <div className="admin-print-card-rolle">{c.rolle}</div>
        </div>
      ))}
    </div>
  );
}
